import { Component } from '@theme/component';

/**
 * The header search (SPEC §3.2, §3.5; Figma 693:35153, 693:35370).
 *
 * From 1200 up the field in header row 1 is a combobox: it is the input of the
 * `predictive-search-component` it wraps, and focusing it opens a panel that
 * drops out of the header — popular searches and categories while empty,
 * product and category rows while typing. The panel is a listbox popup, not a
 * dialog: focus stays in the field, Tab and Shift+Tab walk the results (vendor
 * `onSearchKeyDown`), Escape closes it, and a press outside it closes it.
 *
 * Below 1200 the frames draw the same panel docked under the 121px header
 * (849:11114 empty, 849:13061 typing): the header field stays the one being
 * typed in and the panel runs edge to edge beneath it. So the combobox is the
 * same at every width; only its geometry changes, in `snippets/search.liquid`.
 * This replaces the hand-off to `#search-modal` below 1200 (OI-37).
 *
 * With scripting off the field is a plain GET form to /search.
 *
 * ## Closing (OI-124)
 *
 * The panel carries its own close button, and Escape closes it wherever focus
 * is on the page — a press on the card's blank space blurs the field, and the
 * Escape handler on this element alone missed that. Below 1200, opening pushes
 * one history entry so the phone's Back button or back swipe closes the panel
 * instead of leaving the page.
 */
class AdmiraHeaderSearch extends Component {
  #controller = new AbortController();

  /** True while the history entry pushed by `open()` is the current one. */
  #pushedHistory = false;

  /** Set while focus is put back in the field, so that focus does not reopen. */
  #refocusing = false;

  #compact = window.matchMedia('(max-width: 1199px)');

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    // A press anywhere outside the field, the button or the card closes it.
    // The panel box around the card lets presses through, so beside the card
    // counts as outside. A press on a link keeps the pushed history entry:
    // stepping back while that link navigates could cancel the navigation.
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.hasAttribute('data-open')) return;
        const target = /** @type {Element} */ (event.target);
        if (this.#body?.contains(target) || this.#form?.contains(target)) return;
        this.close({ keepHistory: Boolean(target.closest?.('a[href]')) });
      },
      { signal }
    );

    // Escape closes and keeps the caret in the field. On the document, in the
    // capture phase: it has to work with focus anywhere, and run before the
    // vendor handler clears the query.
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.#refocus();
      },
      { capture: true, signal }
    );

    // Back (button or swipe) while the panel is open closes it.
    window.addEventListener(
      'popstate',
      () => {
        if (!this.#pushedHistory) return;
        this.#pushedHistory = false;
        this.close({ fromHistory: true });
      },
      { signal }
    );

    // Focus leaving the whole control (Tab past the last result) closes it.
    this.addEventListener(
      'focusout',
      (event) => {
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (next && this.contains(next)) return;
        if (!next) return; // A click inside the panel blurs to nothing first.
        this.close();
      },
      { signal }
    );

    // Typing after Escape reopens the panel without needing a new focus.
    this.addEventListener(
      'input',
      () => {
        this.open();
      },
      { signal }
    );

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('#admira-header-search-input'));
  }

  get #panel() {
    return this.querySelector('.admira-search-panel');
  }

  get #body() {
    return this.querySelector('.admira-search-panel__body');
  }

  get #scrim() {
    return this.querySelector('.admira-search-scrim');
  }

  get #form() {
    return this.querySelector('form');
  }

  /**
   * Bound to the field's `focus` and `click` (declarative `on:focus`,
   * `on:click`): after Escape or the close button focus is back in the field,
   * so a click on it fires no new focus and has to reopen the panel itself.
   */
  onFocus() {
    if (this.#refocusing) return;
    this.open();
  }

  /**
   * Bound to the panel's close button. On a touch screen focus is not put back
   * in the field: that would raise the keyboard the reader just dismissed.
   */
  closeFromButton() {
    this.close();
    if (!window.matchMedia('(pointer: coarse)').matches) this.#refocus();
  }

  /** Puts focus back in the field without reopening the panel. */
  #refocus() {
    const input = this.#input;
    if (!input || document.activeElement === input) return;
    this.#refocusing = true;
    input.focus({ preventScroll: true });
    this.#refocusing = false;
  }

  open() {
    if (this.hasAttribute('data-open')) return;
    const panel = this.#panel;
    const scrim = this.#scrim;
    if (!panel) return;

    panel.hidden = false;
    if (scrim) scrim.hidden = false;
    this.setAttribute('data-open', '');
    this.#input?.setAttribute('aria-expanded', 'true');
    this.closest('#header-component')?.setAttribute('data-search-open', '');

    if (this.#compact.matches && !this.#pushedHistory) {
      history.pushState({ ...history.state, admiraSearch: true }, '');
      this.#pushedHistory = true;
    }
  }

  /**
   * @param {{ fromHistory?: boolean, keepHistory?: boolean }} [options]
   *   `fromHistory`: the history entry is already gone (Back was pressed).
   *   `keepHistory`: leave the pushed entry; a navigation is about to start.
   */
  close({ fromHistory = false, keepHistory = false } = {}) {
    const panel = this.#panel;
    const scrim = this.#scrim;
    if (panel) panel.hidden = true;
    if (scrim) scrim.hidden = true;
    this.removeAttribute('data-open');
    this.#input?.setAttribute('aria-expanded', 'false');
    this.closest('#header-component')?.removeAttribute('data-search-open');

    if (this.#pushedHistory && !fromHistory) {
      this.#pushedHistory = false;
      if (!keepHistory && history.state?.admiraSearch) history.back();
    }
  }
}

if (!customElements.get('admira-header-search')) {
  customElements.define('admira-header-search', AdmiraHeaderSearch);
}
